import { useEffect, useState } from "react";
import { Outlet, ScrollRestoration, useLocation, useMatches } from "react-router";
import { Drawer } from "@/components";
import { useBalanceConnection } from "@/devices";
import { useDocumentTitle, useHotkey } from "@/lib/hooks";
import { rememberSearch } from "@/lib/listSearch";
import type { RouteHandle } from "@/lib/routing";
import { AppCommandPalette } from "../AppCommandPalette";
import { AppHeader } from "./AppHeader";
import { SidebarNav } from "./SidebarNav";
import styles from "./AppLayout.module.css";

function usePageTitle(): string | undefined {
  const matches = useMatches();
  for (let index = matches.length - 1; index >= 0; index -= 1) {
    const title = (matches[index]?.handle as RouteHandle | undefined)?.title;
    if (title) return title;
  }
  return undefined;
}

/** The authenticated shell: header, grouped sidebar (a drawer below 64rem), page outlet and the palette. */
export function AppLayout() {
  useBalanceConnection();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [navigationOpen, setNavigationOpen] = useState(false);
  const { pathname, search } = useLocation();
  useEffect(() => rememberSearch(pathname, search), [pathname, search]);
  useDocumentTitle(usePageTitle());
  useHotkey("k", () => setPaletteOpen((open) => !open), { mod: true, allowInFields: true });

  return (
    <div className={styles.shell}>
      <a href="#main" className={styles.skipLink}>
        Skip to content
      </a>
      <AppHeader onOpenPalette={() => setPaletteOpen(true)} onOpenNavigation={() => setNavigationOpen(true)} />
      <div className={styles.body}>
        <aside className={styles.sidebar} aria-label="Sections">
          <SidebarNav />
        </aside>
        <main id="main" className={styles.main} tabIndex={-1}>
          <Outlet />
        </main>
      </div>
      <Drawer open={navigationOpen} onOpenChange={setNavigationOpen} title="Navigation" hideTitle>
        <SidebarNav onNavigate={() => setNavigationOpen(false)} />
      </Drawer>
      <AppCommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
      <ScrollRestoration />
    </div>
  );
}
